import React, { useState } from 'react';
import { useLineRead, type Obj } from '@/client/api/whatsapp/line-actions';
import { Empty, ErrorNote, Loading, Section, asArray } from './shared';

const Item: React.FC<{ o: Obj }> = ({ o }) => {
  const img = o.image?.originalImageUrl || o.imageUrls?.requested || o.imageUrl || o.image;
  return (
    <li className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-2 text-sm">
      {typeof img === 'string' && <img src={img} alt="" className="h-12 w-12 rounded object-cover" />}
      <div className="min-w-0">
        <div className="truncate font-medium text-gray-900">{o.name || o.title || o.id}</div>
        {(o.price != null || o.description) && <div className="truncate text-xs text-gray-500">{[o.price != null ? `${o.price} ${o.currency ?? ''}` : '', o.description].filter(Boolean).join(' - ')}</div>}
      </div>
    </li>
  );
};

const BusinessTab: React.FC<{ instance: string; connected: boolean }> = ({ instance, connected }) => {
  const [limit, setLimit] = useState(20);
  const profile = useLineRead<Obj>(instance, 'chat.fetchBusinessProfile', { body: {}, enabled: connected });
  const catalog = useLineRead<Obj>(instance, 'business.getCatalog', { body: { limit }, enabled: connected });
  const collections = useLineRead<Obj>(instance, 'business.getCollections', { body: { limit }, enabled: connected });
  if (!connected) return <Empty title="No phone is linked" hint="Link a phone from the Overview tab to see its business profile." />;
  const p = profile.data;
  const products = asArray<Obj>(catalog.data?.products ?? catalog.data);
  const cols = asArray<Obj>(collections.data?.collections ?? collections.data);
  return (
    <div className="space-y-6">
      <Section title="Business profile">
        {profile.isLoading && <Loading what="the business profile" />}
        {profile.isError && <ErrorNote error={profile.error} what="the business profile" />}
        {p && p.isBusiness === false && <Empty title="This is a personal account" hint="Catalog and collections need a WhatsApp Business account. Link a Business phone to use them." />}
        {p && p.isBusiness !== false && (
          <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 text-sm">
            {([['Name', p.name], ['Description', p.description], ['Email', p.email], ['Address', p.address], ['Category', p.vertical], ['Website', [].concat(p.websites ?? p.website ?? []).join(', ')]] as [string, string][]).map(([k, v]) => (
              <React.Fragment key={k}><dt className="text-gray-500">{k}</dt><dd className="text-gray-900">{v || 'Not set'}</dd></React.Fragment>
            ))}
          </dl>
        )}
      </Section>
      {p?.isBusiness !== false && (
        <>
          <Section title="Catalog">
            {catalog.isLoading && <Loading what="the catalog" />}
            {catalog.isError && <ErrorNote error={catalog.error} what="the catalog" />}
            {catalog.data && products.length === 0 && <Empty title="The catalog is empty" hint="Add products in the WhatsApp Business app on the linked phone." />}
            <ul className="space-y-2">{products.map((o) => <Item key={o.id ?? o.name} o={o} />)}</ul>
            {products.length >= limit && <button type="button" className="text-sm text-blue-700 underline" onClick={() => setLimit(limit + 20)}>Show more</button>}
          </Section>
          <Section title="Collections">
            {collections.isLoading && <Loading what="collections" />}
            {collections.isError && <ErrorNote error={collections.error} what="collections" />}
            {collections.data && cols.length === 0 && <Empty title="No collections" hint="Group products into collections in the WhatsApp Business app." />}
            <ul className="space-y-2">{cols.map((o) => <Item key={o.id ?? o.name} o={o} />)}</ul>
          </Section>
        </>
      )}
    </div>
  );
};

export default BusinessTab;
